import {
  Appender,
  AppenderOptions,
  LogEntry,
} from '../../types/LoggerTypes';
import {AppenderType, LogLevel} from "@mosaic/logger-service";

export interface ApiAppenderOptions extends Omit<AppenderOptions, "template"> {
  endpoint: string;
  batchSize: number;
  flushIntervalMs?: number;
  headers?: Record<string, string> | (() => Record<string, string> | Promise<Record<string, string>>);
}

export const STD_API_APPENDER_NAME = 'STD_API_APPENDER_NAME';

export const STD_API_APPENDER_DEFAULT_OPTIONS: ApiAppenderOptions = {
  name: STD_API_APPENDER_NAME,
  type: AppenderType.API,
  endpoint: '',
  batchSize: 10,
  flushIntervalMs: 5000,
  headers: {},
  threshold: LogLevel.OFF,
}

/**
 * ApiAppender sends log entries to an HTTP endpoint in batches.
 * It supports a configurable batch size, flush interval and headers resolver
 * which can be static or a function (sync or async) returning headers (e.g. auth token).
 */
export class ApiAppender implements Appender {

  private options: ApiAppenderOptions;
  private buffer: LogEntry[] = [];
  private timer: any = undefined;

  /**
   * Create an ApiAppender instance.
   * @param options partial options; defaults are merged with STD_API_APPENDER_DEFAULT
   */
  constructor(options?: Partial<Omit<ApiAppenderOptions, 'type'>>) {
    this.options = { ...STD_API_APPENDER_DEFAULT_OPTIONS, ...(options ?? {}) };
  }

  /**
   * Append a log entry to the internal buffer and schedule a flush
   * when batchSize or flushIntervalMs conditions are met.
   */
  append(entry: LogEntry): void {

    this.buffer.push(entry);

    if (this.buffer.length >= (this.options.batchSize ?? 1)) {
      this.flush();
      return;
    }

    // schedule flush
    if (!this.timer) {
      this.timer = setTimeout(() => {
        this.flush();
      }, this.options.flushIntervalMs ?? 5000);
    }
  }

  /**
   * Resolve headers which can be a static object or a function returning headers.
   */
  private async resolveHeaders(): Promise<Record<string, string>> {
    const h = this.options.headers ?? {};
    if (typeof h === 'function') {
      const res = h();
      if (res instanceof Promise) return await res;
      return res as Record<string, string>;
    }
    return h as Record<string, string>;
  }

  /**
   * Flush the current buffer to the configured endpoint.
   * Uses navigator.sendBeacon when available as first option, otherwise falls back to fetch.
   */
  private async flush() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    if (this.buffer.length === 0) return;

    const payload = this.buffer.splice(0, this.buffer.length);

    const endpoint = this.options.endpoint;
    if (!endpoint) return; // nothing to do

    const headers = await this.resolveHeaders();
    try {
      // use navigator.sendBeacon if available and method is POST-safe
      if (typeof navigator !== 'undefined' && typeof (navigator as any).sendBeacon === 'function') {
        const body = JSON.stringify(payload);
        try {
          (navigator as any).sendBeacon(endpoint, body);
          return;
        } catch (e) {
          // fallback to fetch
        }
      }

      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(headers ?? {}) },
        body: JSON.stringify(payload)
      });
    } catch (e) {
      // failed to send: drop or requeue? for now drop and log to console
       
      console.warn('ApiAppender flush failed', e);
    }
  }

  /** Return configured threshold for this appender. */
  public getThreshold(): LogLevel {
    return this.options.threshold ?? LogLevel.DEBUG;
  }

  /** Set a new threshold and return the previous one. */
  public setThreshold(newLevel: LogLevel): LogLevel {
    const old = this.options.threshold ?? LogLevel.DEBUG;
    this.options.threshold = newLevel;
    return old;
  }

  /** Return the appender name. */
  public name(): string {
    return this.options.name;
  }
}
